import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Notice, Progress } from "../components/Notice";
import { Player } from "../components/Player";
import { api, ApiError, describeError, type GenerationAccess, type LibraryItem } from "../lib/api";

const MIN_SECONDS = 5;
const MAX_SECONDS = 30;
const DEFAULT_SECONDS = 20;
const POLL_MS = 2000;

/**
 * One generation attempt as the browser remembers it. Scoped to the
 * signed-in SoundLayer ID so another account on the same browser never
 * sees or replays it. The attempt id (idempotency key) is what lets a
 * refresh, a lost response or a failed poll reconcile against the server
 * instead of starting a second paid request.
 */
interface Pending {
  key: string;
  prompt: string;
  duration: number;
  loop: boolean;
  startedAt: number;
}

function pendingStorageKey(userId: string) {
  return `soundlayer:create:pending:${userId}`;
}
function readPending(userId: string): Pending | null {
  try {
    const raw = sessionStorage.getItem(pendingStorageKey(userId));
    return raw ? (JSON.parse(raw) as Pending) : null;
  } catch {
    return null;
  }
}
function writePending(userId: string, p: Pending | null) {
  try {
    if (p) sessionStorage.setItem(pendingStorageKey(userId), JSON.stringify(p));
    else sessionStorage.removeItem(pendingStorageKey(userId));
  } catch {
    /* storage unavailable: a refresh mid-generation cannot be reconciled */
  }
}

type Phase =
  | { kind: "idle" }
  | { kind: "generating"; startedAt: number; itemId: string | null; resumed: boolean }
  | { kind: "ready"; item: LibraryItem }
  /** The server confirmed nothing was generated. A retry is a new attempt. */
  | { kind: "failed"; code: string | null; message: string }
  /** The provider was called and never answered. May have been charged. */
  | { kind: "unresolved"; item: LibraryItem }
  /** We never heard back from SoundLayer. The attempt id is kept; check, do not resend. */
  | { kind: "unknown"; message: string };

export function Create() {
  const { session } = useAuth();
  const userId = session?.user.id ?? "anonymous";
  const [access, setAccess] = useState<GenerationAccess | null>(null);
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(DEFAULT_SECONDS);
  const [loop, setLoop] = useState(true);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [elapsed, setElapsed] = useState(0);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [recovered, setRecovered] = useState<LibraryItem | null>(null);
  const pollTimer = useRef<number | null>(null);
  const started = useRef<string | null>(null);

  const clearPoll = () => {
    if (pollTimer.current) window.clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };

  /** Apply a server-reported item state. Only a final state clears the attempt marker. */
  const settle = useCallback(
    (item: LibraryItem, pending: Pending) => {
      clearPoll();
      if (item.status === "ready") {
        writePending(userId, null);
        setTitle(item.title);
        setPhase({ kind: "ready", item });
      } else if (item.status === "failed") {
        writePending(userId, null);
        setPhase({ kind: "failed", code: item.error_code, message: describeError(item.error_code) });
      } else if (item.status === "unresolved") {
        writePending(userId, null);
        setPhase({ kind: "unresolved", item });
      } else if (item.resumable) {
        // Claimed but the provider was never called: resuming costs nothing extra.
        void send(pending, true);
      } else {
        setPhase({ kind: "generating", startedAt: pending.startedAt, itemId: item.id, resumed: true });
        pollTimer.current = window.setTimeout(() => {
          api
            .getItem(item.id)
            .then((next) => settle(next, pending))
            .catch((e: unknown) => {
              if (e instanceof ApiError && (e.status === 404 || e.status === 410)) {
                // The row was discarded while we waited.
                writePending(userId, null);
                setPhase({ kind: "idle" });
                return;
              }
              setPhase({
                kind: "unknown",
                message:
                  e instanceof ApiError && (e.status === 401 || e.status === 403)
                    ? "Your sign-in expired while waiting for the result. Sign in again and check this attempt's status; it will not be sent twice."
                    : "Lost contact while waiting for the result. The attempt is still recorded; check its status rather than starting another.",
              });
            });
        }, POLL_MS);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  );

  /** POST the attempt. `resume` only changes the wording; the server decides what it costs. */
  const send = useCallback(
    async (p: Pending, resume = false) => {
      writePending(userId, p);
      setPhase({ kind: "generating", startedAt: p.startedAt, itemId: null, resumed: resume });
      try {
        const item = await api.generate({ prompt: p.prompt, duration_seconds: p.duration, loop: p.loop }, p.key);
        settle(item, p);
      } catch (e: unknown) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403) && e.code !== "generation_not_allowed") {
          // Sign-in expired or was rejected mid-attempt. The server may hold
          // the row; keep the marker and reconcile after signing in again.
          setPhase({
            kind: "unknown",
            message: "Your sign-in expired before SoundLayer answered. Sign in again and check this attempt's status; it will not be sent twice.",
          });
        } else if (e instanceof ApiError && e.code === "resume_deferred") {
          // The attempt exists and is waiting for a slot or allowance.
          setPhase({
            kind: "unknown",
            message: `${e.message} Your earlier attempt is kept and will resume when you check again.`,
          });
        } else if (e instanceof ApiError && e.answered) {
          // Refused before any row was written (gate, limit, bad request), or
          // the key belongs to a different or discarded request. Nothing is
          // in flight under this id.
          writePending(userId, null);
          if (e.status === 410) setPhase({ kind: "idle" });
          else setPhase({ kind: "failed", code: e.code, message: e.code ? describeError(e.code) : e.message });
        } else {
          // Network drop, gateway or server error, or the page unloading. The
          // server may still be working, so keep the attempt id.
          setPhase({
            kind: "unknown",
            message: "SoundLayer did not answer before the connection dropped. The attempt is still recorded under its id; check its status instead of sending it again.",
          });
        }
      }
    },
    [settle, userId],
  );

  /** Reconcile a remembered attempt with the server without resending it blindly. */
  const reconcile = useCallback(
    async (p: Pending) => {
      setPrompt(p.prompt);
      setDuration(p.duration);
      setLoop(p.loop);
      setPhase({ kind: "generating", startedAt: p.startedAt, itemId: null, resumed: true });
      try {
        const item = await api.getAttempt(p.key);
        settle(item, p);
      } catch (e: unknown) {
        if (e instanceof ApiError && e.status === 404) {
          // The request never reached the server: sending it is the first and only attempt.
          void send(p);
        } else if (e instanceof ApiError && (e.status === 409 || e.status === 410)) {
          // The id belongs to a different request or a discarded one. Nothing to recover.
          writePending(userId, null);
          setPhase({ kind: "idle" });
        } else if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          // Not proof that the attempt stopped. Keep it; reconcile after sign-in.
          setPhase({
            kind: "unknown",
            message: "SoundLayer could not verify your sign-in while checking this attempt. Sign in again, then check its status; it will not be sent twice.",
          });
        } else {
          setPhase({
            kind: "unknown",
            message: "Could not reach SoundLayer to check this attempt. Try checking again in a moment.",
          });
        }
      }
    },
    [send, settle, userId],
  );

  // Mount, per signed-in user: reconcile a remembered attempt, otherwise
  // offer the most recent unsaved preview so paid audio is not lost.
  useEffect(() => {
    if (started.current === userId) return;
    started.current = userId;
    setPhase({ kind: "idle" });
    setRecovered(null);
    api
      .me()
      .then((me) => setAccess(me.generation))
      .catch(() => setAccess(null));
    const pending = readPending(userId);
    if (pending) {
      void reconcile(pending);
      return;
    }
    setPrompt("");
    api
      .listLibrary(true)
      .then((items) => setRecovered(items.find((i) => !i.saved) ?? null))
      .catch(() => setRecovered(null));
    return clearPoll;
  }, [userId, reconcile]);

  useEffect(() => {
    if (phase.kind !== "generating") return;
    const id = window.setInterval(() => setElapsed(Math.round((Date.now() - phase.startedAt) / 1000)), 500);
    return () => window.clearInterval(id);
  }, [phase]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (phase.kind === "generating") return;
    void send({ key: crypto.randomUUID(), prompt: prompt.trim(), duration, loop, startedAt: Date.now() });
  }

  function checkAgain() {
    const p = readPending(userId);
    if (p) void reconcile(p);
    else setPhase({ kind: "idle" });
  }

  async function save() {
    if (phase.kind !== "ready") return;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await api.save(phase.item.id, title.trim() || phase.item.title);
      setPhase({ kind: "ready", item: saved });
    } catch (e: unknown) {
      setSaveError(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  async function discard(item: LibraryItem) {
    try {
      await api.remove(item.id);
    } finally {
      if (phase.kind === "ready" && phase.item.id === item.id) setPhase({ kind: "idle" });
      if (recovered?.id === item.id) setRecovered(null);
    }
  }

  const generating = phase.kind === "generating";
  const blocked = access !== null && !access.allowed;
  const canSubmit = prompt.trim().length >= 3 && !generating && !blocked && phase.kind !== "unknown";

  return (
    <section className="stack">
      <div>
        <h1>Create</h1>
        <p className="lede">Describe the sound you want to hear. SoundLayer generates it and keeps it in your library.</p>
      </div>

      {blocked && (
        <Notice
          kind="info"
          title={access?.enabled ? "Generation is not switched on for this SoundLayer ID yet." : "Generation is switched off right now."}
        >
          {access?.enabled
            ? "Your existing library still works. Generation is being opened up in stages; you will be able to create new sounds when your ID is switched on."
            : "Your existing library still works. Creating new sounds will be back when generation is switched on again."}
        </Notice>
      )}

      {recovered && phase.kind === "idle" && (
        <div className="card reveal stack">
          <div className="row spread">
            <h3>You have an unsaved sound</h3>
            <span className="pill pill-warn">Unsaved</span>
          </div>
          <p className="prompt muted small">{recovered.prompt}</p>
          <Player itemId={recovered.id} loop={recovered.loop} />
          <div className="row">
            <button
              className="btn btn-primary"
              onClick={() => {
                setTitle(recovered.title);
                setPhase({ kind: "ready", item: recovered });
                setRecovered(null);
              }}
            >
              Name and save
            </button>
            <button className="btn btn-danger" onClick={() => discard(recovered)}>
              Discard
            </button>
          </div>
        </div>
      )}

      <form className="card stack" onSubmit={submit} aria-busy={generating}>
        <div className="field">
          <label htmlFor="prompt">What should it sound like?</label>
          <textarea
            id="prompt"
            className="textarea"
            placeholder="Gentle rain against a window with a distant fireplace, no music or voices."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={1000}
            required
            minLength={3}
            disabled={generating || blocked}
          />
          <span className="hint">Say what to include and what to leave out. Ambience, textures and pacing all help.</span>
        </div>
        <div className="field">
          <label htmlFor="duration">Length: {duration} seconds</label>
          <input
            id="duration"
            className="range"
            type="range"
            min={MIN_SECONDS}
            max={MAX_SECONDS}
            step={1}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            disabled={generating || blocked}
          />
          <span className="hint">
            Each generation is up to {MAX_SECONDS} seconds. This one reserves an estimated{" "}
            {duration * (access?.credits_per_second ?? 40)} units of your generation allowance; what the provider actually
            bills is confirmed from account usage, not from this estimate. Loop it for continuous listening.
          </span>
        </div>
        <label className="switch">
          <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} disabled={generating || blocked} />
          Make it loop seamlessly
        </label>

        {phase.kind === "generating" && (
          <Progress
            label={
              phase.resumed
                ? `Checking on your sound… ${elapsed}s. Leave this page open.`
                : `Generating… ${elapsed}s. This usually takes 10 to 40 seconds. Leave this page open.`
            }
          />
        )}
        {phase.kind === "failed" && (
          <Notice kind="error" title="Generation did not happen. Nothing was added to your library.">
            {phase.message}
            {phase.code && <span className="mono"> ({phase.code})</span>}
          </Notice>
        )}
        {phase.kind === "unknown" && (
          <Notice kind="error" title="Result unknown">
            {phase.message}
          </Notice>
        )}
        {phase.kind === "unresolved" && (
          <Notice kind="error" title="We can't confirm whether this sound was generated.">
            {describeError(phase.item.error_code)} Starting a new attempt may use credits again.
          </Notice>
        )}

        {phase.kind === "unknown" ? (
          <button className="btn btn-primary btn-block" type="button" onClick={checkAgain}>
            Check status
          </button>
        ) : (
          <button className="btn btn-primary btn-block" type="submit" disabled={!canSubmit}>
            {generating
              ? phase.resumed
                ? "Checking…"
                : "Generating…"
              : phase.kind === "failed"
                ? "Try again"
                : phase.kind === "unresolved"
                  ? "Start a new attempt"
                  : "Generate"}
          </button>
        )}
      </form>

      {phase.kind === "ready" && (
        <div className="card reveal stack" aria-live="polite">
          <div className="row spread">
            <h3>{phase.item.saved ? "Saved to your library" : "Preview"}</h3>
            <span className={`pill ${phase.item.saved ? "pill-ok" : "pill-warn"}`}>
              {phase.item.saved ? "Saved" : "Unsaved"}
            </span>
          </div>
          <Player itemId={phase.item.id} loop={phase.item.loop} />
          <div className="field">
            <label htmlFor="title">Name</label>
            <input id="title" className="input" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
          </div>
          {saveError && <Notice kind="error">{saveError}</Notice>}
          <div className="row">
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : phase.item.saved ? "Rename" : "Save to library"}
            </button>
            {phase.item.saved ? (
              <Link className="btn" to="/library">
                Open library
              </Link>
            ) : (
              <button className="btn btn-danger" onClick={() => discard(phase.item)}>
                Discard
              </button>
            )}
          </div>
          <p className="small muted">
            {phase.item.duration_seconds}s · {phase.item.loop ? "loops" : "plays once"} · reserved estimate{" "}
            {phase.item.generation_credits ?? "?"} allowance units (not a provider bill)
          </p>
        </div>
      )}
    </section>
  );
}
